import React, { useState, useEffect } from 'react';
import SubmissionCard from './SubmissionCard';
import Header from './Header';
import { CONFIG, ERROR_MESSAGES, SUCCESS_MESSAGES } from '../constants/config';
import { ApiService } from '../services/api';

const Leaderboard = () => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: CONFIG.SUBMISSIONS_PER_PAGE,
    total: 0,
    pages: 0
  });

  const fetchSubmissions = async (page = 1, search = '') => {
    try {
      setLoading(true);
      const response = await ApiService.getSubmissions(page, search);

      // Add safety checks for response structure
      const responseSubmissions = response?.submissions || [];
      const responsePagination = response?.pagination || {
        page: 1,
        per_page: CONFIG.SUBMISSIONS_PER_PAGE,
        total: 0,
        pages: 0
      };

      if (page === 1) {
        setSubmissions(responseSubmissions);
      } else {
        setSubmissions(prev => [...(prev || []), ...responseSubmissions]);
      }
      
      setPagination(responsePagination);
      setError(null);
    } catch (err) {
      console.error('Error fetching submissions:', err);
      setError(ERROR_MESSAGES.LOAD_FAILED);
      // Ensure submissions is always an array even on error
      if (page === 1) {
        setSubmissions([]);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubmissions(1, '');
    
    // Check for payment status in URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const payment = urlParams.get('payment');
    
    if (payment === 'success') {
      setPaymentStatus('success');
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
      
      // Refresh submissions after successful payment
      setTimeout(() => {
        fetchSubmissions(1, '');
      }, 2000); // Wait 2 seconds for webhook to process
    } else if (payment === 'cancelled') {
      setPaymentStatus('cancelled');
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = (e) => {
    e.preventDefault();
    fetchSubmissions(1, searchTerm);
  };

  const handleLoadMore = () => {
    if (pagination.page < pagination.pages) {
      fetchSubmissions(pagination.page + 1, searchTerm);
    }
  };

  const handleDonate = async (submissionId, amount) => {
    // This is handled by the SubmissionCard in testing mode
    console.log('Payment handled by SubmissionCard component');
  };

  return (
    <div>
      <Header />
      
      <div className="container">
        <div className="main-content">
          {/* Payment Status Messages */}
          {paymentStatus === 'success' && (
            <div className="success" style={{ marginBottom: '20px' }}>
              🎉 Payment successful! Your submission/payment has been processed. The leaderboard will refresh automatically in a moment.
              <button 
                onClick={() => {
                  setPaymentStatus(null);
                  fetchSubmissions(1, searchTerm);
                }} 
                style={{ 
                  marginLeft: '15px', 
                  background: '#22c55e', 
                  border: 'none', 
                  color: 'white', 
                  cursor: 'pointer',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  fontSize: '0.8rem'
                }}
              >
                Refresh Now
              </button>
              <button 
                onClick={() => setPaymentStatus(null)} 
                style={{ 
                  marginLeft: '10px', 
                  background: 'none', 
                  border: 'none', 
                  color: '#66ff66', 
                  cursor: 'pointer',
                  fontSize: '1.2em'
                }}
              >
                ×
              </button>
            </div>
          )}
          
          {paymentStatus === 'cancelled' && (
            <div className="error" style={{ marginBottom: '20px' }}>
              Payment was cancelled. You can try again anytime.
              <button 
                onClick={() => setPaymentStatus(null)} 
                style={{ 
                  marginLeft: '10px', 
                  background: 'none', 
                  border: 'none', 
                  color: '#ff6666', 
                  cursor: 'pointer',
                  fontSize: '1.2em'
                }}
              >
                ×
              </button>
            </div>
          )}

          <div className="search-section">
            <form onSubmit={handleSearch} style={{ display: 'flex', gap: '10px', flex: 1 }}>
              <input
                type="text"
                placeholder="Search submissions by title..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
              <button type="submit" className="btn btn-secondary">
                Search
              </button>
            </form>
            <button 
              onClick={() => fetchSubmissions(1, searchTerm)}
              disabled={loading}
              className="btn btn-primary"
              title="Refresh leaderboard"
            >
              {loading ? '🔄' : '🔄 Refresh'}
            </button>
          </div>

          {error && <div className="error">{error}</div>}

          {loading && (submissions || []).length === 0 ? (
            <div className="loading">
              <div>Loading the leaderboard...</div>
              <div style={{ marginTop: '10px', fontSize: '0.9rem', opacity: 0.7 }}>
                Calculating total payments...
              </div>
            </div>
          ) : (
            <>
              <div className="submissions-list">
                {(submissions || []).map((submission, index) => (
                  <SubmissionCard
                    key={submission.id}
                    submission={submission}
                    rank={index + 1}
                    onDonate={handleDonate}
                  />
                ))}
              </div>

              {(submissions || []).length === 0 && !loading && (
                <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>
                  {searchTerm ? 'No submissions found matching your search.' : 'No submissions yet. Be the first to create one!'}
                </div>
              )}

              {pagination.page < pagination.pages && (
                <div className="pagination">
                  <button
                    onClick={handleLoadMore}
                    disabled={loading}
                    className="btn btn-primary load-more-btn"
                  >
                    {loading ? 'Loading...' : `Load Next ${Math.min(CONFIG.SUBMISSIONS_PER_PAGE, pagination.total - (submissions || []).length)} Submissions`}
                  </button>
                </div>
              )}

              {(submissions || []).length > 0 && (
                <div style={{ textAlign: 'center', marginTop: '20px', fontSize: '0.9rem', opacity: 0.7 }}>
                  Showing {(submissions || []).length} of {pagination.total} submissions
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Leaderboard; 